import React, { useCallback, useState } from "react";
import { NavLink, useHistory } from "react-router-dom";
import {
  Col,
  Dropdown,
  DropdownButton,
  FormControl,
  InputGroup,
  Row,
} from "react-bootstrap";
import { SearchIcon, MenuDots } from "../../components/SvgIcons";
import {
  GetDeletedUsers,
  RestoreDeletedUser,
} from "../../services/users.service";
import { EmptyLocalStorage } from "../../services/auth/auth.service";
import { toast } from "react-toastify";
import moment from "moment";
import DataTable from "react-data-table-component";
import BarLoader from "react-spinners/BarLoader";
import {
  constants,
  customStyles,
  rectSelectStyles,
  userTypeData,
} from "../../utils/constants";
import ThemeModal from "../../components/ThemeModal";
import Select from "react-select";
import swal from "sweetalert";
import {
  LIST_TABLE_SCROLL_HEIGHT,
  useInfinitePagedList,
  useListPageScrollLock,
} from "../../hooks/useInfinitePagedList";
import {
  LazyUserAvatar,
  ListLoadMoreIndicator,
} from "../../components/ListInfiniteHelpers";

function DeletedUsers() {
  const history = useHistory();
  const [keyword, setKeyword] = useState("");
  const [selectedUserType, setSelectedUserType] = useState(null);
  const [imageModal, setImageModal] = useState({ url: "", status: false });

  useListPageScrollLock();

  const fetchPage = useCallback(
    async ({ page, pageSize, keyword: searchKeyword }) => {
      try {
        const result = await GetDeletedUsers(
          searchKeyword,
          constants.ROLES.ROLE_USER,
          page,
          pageSize,
          "",
          "",
          selectedUserType?.value
        );
        if (!result.success) {
          toast.error(result.message);
          return { rows: [], total: 0 };
        }
        const rows = (result.data || []).map((dt, index) => ({
          id: dt.id,
          user_short_id: dt.user_short_id,
          index: (page - 1) * pageSize + index + 1,
          CategoryImg: dt.profile_image || constants.ClientImage,
          full_name: dt.full_name || "-",
          state: dt.state || "-",
          email: dt.email || "-",
          phone: dt.phone_number || "-",
          dob: dt.dob || "-",
          spnNo: dt.license || "-",
          program: dt.program || "-",
          county: dt.county || "-",
          officer: dt.officer_name || "-",
          representative: dt.representative || "-",
          created_at: moment(dt.created_at).fromNow(),
          user_type: dt?.user_type,
        }));
        return { rows, total: result.total || 0 };
      } catch (error) {
        if (error.response?.status === 401) {
          EmptyLocalStorage();
          history.push("/");
        } else {
          toast.error(error.response?.data?.message || error.message);
        }
        throw error;
      }
    },
    [history, selectedUserType]
  );

  const {
    rows,
    loading,
    loadingMore,
    hasMore,
    totalRows,
    tableWrapperRef,
    refresh,
  } = useInfinitePagedList({
    fetchPage,
    keyword,
    resetDeps: [selectedUserType?.value],
  });

  const clearInput = () => setKeyword("");

  const ModalForImage = () => (
    <div className={"modalBodyContent"} style={{ textAlign: "center" }}>
      <img
        style={{
          objectFit: "cover",
          objectPosition: "center",
          textAlign: "center",
        }}
        src={imageModal.url}
        className="img-fluid"
        alt=""
      />
    </div>
  );

  const onViewPage = (data) => {
    history.push(`/deleted-users/${data.id}`);
  };

  const restoreDeleteUser = (id) => {
    swal({
      title: "Are you sure?",
      text: "Are you sure you want to restore deleted this user?",
      icon: "warning",
      buttons: true,
      dangerMode: true,
    }).then(async (willShip) => {
      if (willShip) {
        await RestoreDeletedUser({ user: id })
          .then((result) => {
            if (result.success) {
              swal(result.message, { icon: "success" });
              refresh();
            } else {
              toast.error(result.message);
            }
          })
          .catch((error) => {
            if (error.response?.status == 401) {
              EmptyLocalStorage();
              history.push("/");
            } else {
              return toast.error(error.response?.data?.message);
            }
          });
      }
    });
  };

  const userActions = (data) => (
    <DropdownButton
      align="end"
      className={"actionDropdown"}
      id="dropdown-item-button"
      title={<MenuDots />}
    >
      <Dropdown.Item as="button" onClick={() => onViewPage(data)}>
        View User Details
      </Dropdown.Item>
      <Dropdown.Item as="button" onClick={() => restoreDeleteUser(data?.id)}>
        Restore User
      </Dropdown.Item>
    </DropdownButton>
  );

  const columns = [
    {
      name: "S No.",
      width: "50px",
      selector: (rowData) => rowData.user_short_id,
    },
    {
      name: "Picture",
      width: "100px",
      selector: (rowData) => rowData.oid,
      cell: (rowData) => (
        <LazyUserAvatar
          src={rowData.CategoryImg}
          onClick={() =>
            setImageModal({ url: rowData.CategoryImg, status: true })
          }
        />
      ),
    },
    {
      name: "Full Name",
      maxWidth: "160px",
      selector: (rowData) => rowData.full_name,
      cell: (rowData) => (
        <NavLink
          className="text-dark text-decoration-none text-center"
          to={`/deleted-users/${rowData.id}`}
        >
          {rowData.full_name}
        </NavLink>
      ),
    },
    { name: "Date of Birth", selector: (rowData) => rowData.dob },
    { name: "Case No. OR SPN", selector: (rowData) => rowData.spnNo },
    {
      name: "Program",
      selector: (rowData) => (
        <div className="text-uppercase">{rowData.program}</div>
      ),
    },
    { name: "County", selector: (rowData) => rowData.county },
    { name: "Officer", selector: (rowData) => rowData.officer },
    {
      name: "Device",
      selector: (rowData) =>
        rowData.user_type === 10 ? "Portable" : "Interlock",
    },
    { name: "Action", cell: (rowData) => userActions(rowData) },
  ];

  return (
    <div className="usersPage users-page-fixed">
      <h3 className="page-heading">Deleted Users</h3>
      <hr />
      <div className="customOrderFilters users-page-filters">
        <Row>
          <Col xs={12} md={4} lg={5}>
            <InputGroup className="mb-3 search-group">
              <InputGroup.Text id="basic-addon1">
                <SearchIcon />
              </InputGroup.Text>
              <FormControl
                placeholder="Search"
                className="custom-search"
                aria-label="Search"
                maxLength={255}
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                style={{
                  borderRight: keyword ? "none" : "1px solid #ced4da",
                  borderTopRightRadius: keyword ? 0 : "5px",
                  borderBottomRightRadius: keyword ? 0 : "5px",
                }}
              />
              {keyword && (
                <InputGroup.Text
                  className="cross-icon"
                  id="basic-addon2"
                  onClick={clearInput}
                >
                  <svg
                    width="24"
                    height="25"
                    viewBox="0 0 24 25"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 6.48828L18 18.4883"
                      stroke="#817A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M18 6.48828L6 18.4883"
                      stroke="#817A8E"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </InputGroup.Text>
              )}
            </InputGroup>
          </Col>
          <Col xs={12} md={8} lg={7}>
            <Row className="justify-content-end">
              <Col xs={12} md={5} lg={5}>
                <Select
                  name="userTypeSelect"
                  styles={rectSelectStyles}
                  onChange={setSelectedUserType}
                  options={userTypeData}
                  isSearchable={false}
                  placeholder="Filter Users"
                  className="react-select-container mb-3"
                  classNamePrefix="react-select"
                  isClearable
                />
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
      <div
        className="theme-dataGrid products-dataGrid custom-table-border user-table users-infinite-table mt-3"
        ref={tableWrapperRef}
      >
        <DataTable
          columns={columns}
          data={rows}
          progressPending={loading}
          customStyles={customStyles}
          selectableRows={false}
          striped
          highlightOnHover
          pagination={false}
          persistTableHead
          fixedHeader
          fixedHeaderScrollHeight={LIST_TABLE_SCROLL_HEIGHT}
          progressComponent={
            <BarLoader
              color="#961b1a"
              loading={loading}
              css={"marginTop: 10px"}
              height={4}
              width="100%"
            />
          }
        />
        <ListLoadMoreIndicator
          loadingMore={loadingMore}
          hasMore={hasMore}
          totalRows={totalRows}
          entityLabel="users"
        />
      </div>
      <ThemeModal
        title="User Image"
        size="md"
        content={<ModalForImage />}
        show={imageModal.status}
        onHide={() => setImageModal({ url: "", status: false })}
      />
    </div>
  );
}

export default DeletedUsers;
